// Story 4.3: Validation Error Display
// Shows structured pre-submission errors with scroll-to-expense navigation

import { useCallback } from 'react';
import { AlertTriangle, AlertCircle, Car, Receipt, ArrowUpRight, X } from 'lucide-react';

export interface ValidationError {
  expenseId?: string;
  expenseType: 'mileage' | 'petty-cash' | 'claim';
  field?: string;
  expenseLabel?: string;
  message: string;
  fix: string;
}

interface ValidationErrorsProps {
  errors: ValidationError[];
  onDismiss?: () => void;
}

export function ValidationErrors({ errors, onDismiss }: ValidationErrorsProps) {
  const handleErrorClick = useCallback((expenseId: string | undefined) => {
    if (!expenseId) return;
    const el = document.getElementById(`expense-${expenseId}`);
    if (!el) return;

    // Smooth scroll to the expense card
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Temporarily highlight the card with a red ring
    el.classList.add('ring-2', 'ring-destructive', 'ring-offset-2', 'transition-[box-shadow]');
    setTimeout(() => {
      el.classList.remove('ring-2', 'ring-destructive', 'ring-offset-2', 'transition-[box-shadow]');
    }, 2500);
  }, []);

  if (errors.length === 0) return null;

  const claimErrors = errors.filter((e) => e.expenseType === 'claim');
  const expenseErrors = errors.filter((e) => e.expenseType !== 'claim');

  return (
    <div
      id="validation-errors"
      role="alert"
      className="rounded-xl border border-danger/30 bg-danger/5 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-start justify-between px-4 py-3 bg-danger-soft border-b border-danger/20">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-danger shrink-0" />
          <div>
            <p className="text-sm font-semibold text-danger">
              {errors.length} issue{errors.length === 1 ? '' : 's'} to fix before submitting
            </p>
            <p className="text-xs text-danger/70">
              Click an issue to navigate to the expense
            </p>
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-danger/60 hover:text-danger transition-colors p-1 rounded-md hover:bg-danger-soft"
            aria-label="Dismiss errors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Error list */}
      <div className="divide-y divide-destructive/10">
        {/* Claim-level errors first */}
        {claimErrors.map((error, idx) => (
          <div key={`claim-${idx}`} className="px-4 py-3">
            <div className="flex gap-3">
              <AlertCircle className="h-4 w-4 text-danger shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-danger">{error.message}</p>
                <p className="text-xs text-text-2 mt-0.5">
                  <span className="font-medium text-text">Fix: </span>
                  {error.fix}
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* Expense-level errors */}
        {expenseErrors.map((error, idx) => (
          <button
            key={`expense-${idx}`}
            onClick={() => handleErrorClick(error.expenseId)}
            className="w-full text-left px-4 py-3 hover:bg-danger/5 transition-colors group flex items-start justify-between gap-3"
            disabled={!error.expenseId}
          >
            <div className="flex gap-3 min-w-0 flex-1">
              <span className="shrink-0 mt-0.5">
                {error.expenseType === 'mileage' ? (
                  <Car className="h-4 w-4 text-info" />
                ) : (
                  <Receipt className="h-4 w-4 text-info" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                {error.expenseLabel && (
                  <p className="text-xs font-medium text-text-2 mb-0.5 truncate">
                    {error.expenseLabel}
                  </p>
                )}
                <p className="text-sm font-medium text-danger">{error.message}</p>
                <p className="text-xs text-text-2 mt-0.5">
                  <span className="font-medium text-text">Fix: </span>
                  {error.fix}
                </p>
              </div>
            </div>
            {error.expenseId && (
              <ArrowUpRight className="h-4 w-4 shrink-0 text-text-2 group-hover:text-text transition-colors mt-1" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
